// apps/app/src/lib/profile.ts
//
// The signed-in person's own account — clinic user or platform staff alike.

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, http } from './api-client';
import i18n, { isLanguage, type Language } from './i18n';
import { keys } from './query-client';
import { useAuth } from './auth';

export interface Profile {
  id: string;
  email: string;
  role: string;
  firstName: string | null;
  lastName: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  /** Their saved dashboard language; null follows the browser. */
  locale: Language | null;
  clinic: { name: string } | null;
}

export function useProfile() {
  const signedIn = useAuth((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: keys.profile,
    queryFn: () => http.get<Profile>('/auth/profile'),
    enabled: signedIn,
    staleTime: 5 * 60_000,
  });
}

/**
 * Switches to the language saved on their account once it is known — so a
 * choice made on the laptop is there on the reception PC too.
 */
export function useAccountLanguage(): void {
  const { data: profile } = useProfile();
  useEffect(() => {
    if (profile?.locale && isLanguage(profile.locale) && profile.locale !== i18n.language) {
      void i18n.changeLanguage(profile.locale);
    }
  }, [profile?.locale]);
}

/** Changes the language at once, and saves it on their account. */
export function useSetLanguage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (language: Language) => {
      await i18n.changeLanguage(language);
      return http.patch<Profile>('/auth/profile', { locale: language });
    },
    onSuccess: (profile) => queryClient.setQueryData(keys.profile, profile),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : i18n.t('common:account.languageSaveFailed')),
  });
}

/** "Ada Lovelace", or the email when no name has been set. */
export function displayName(profile: Pick<Profile, 'firstName' | 'lastName' | 'email'> | undefined): string {
  if (!profile) return '';
  return [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email;
}

/** Each role's name, as a key in the common namespace (usable from any t). */
export const ROLE_LABEL_KEY: Record<string, string> = {
  PLATFORM: 'common:roles.platform.label',
  OWNER: 'common:roles.owner.label',
  ADMIN: 'common:roles.admin.label',
  PRACTITIONER: 'common:roles.practitioner.label',
  FRONT_DESK: 'common:roles.frontDesk.label',
};

/**
 * A role's name in the current language; an unknown role shows as sent.
 * Call it while rendering, so a language change re-renders it.
 */
export function roleLabel(role: string): string {
  const key = ROLE_LABEL_KEY[role];
  return key ? i18n.t(key) : role;
}

/**
 * @deprecated English only — use `roleLabel(role)` or `t(ROLE_LABEL_KEY[role])`.
 * Kept so screens not yet translated still compile.
 */
export const ROLE_LABEL: Record<string, string> = {
  PLATFORM: 'Platform staff',
  OWNER: 'Owner',
  ADMIN: 'Admin',
  PRACTITIONER: 'Practitioner',
  FRONT_DESK: 'Front desk',
};
